import {makeObservable} from 'mobx';

import type {SubgoalApiModel} from '../../../api/goal';
import type {Theme} from '../../../styles';
import type {Nullable} from '../../../utils/utility-types';
import type {DateTimeModel} from '../Date/DateTime.model';

import {getSubgoalColor, getSubgoalIcon} from '../../../utils/entity/goal';
import {BaseModel} from '../Base/Base.model';

const ModelName = 'SubgoalModel';

export class SubgoalModel extends BaseModel {
  checkpoints: SubgoalModel.Checkpoint[];
  clientAttitude: Nullable<number>;
  clientComment: string;
  clientId: Nullable<string>;
  coachAttitude: Nullable<number>;
  coachComment: string;
  coachIds: string[];
  color: Nullable<string>;
  counters: {
    childSubgoals: number;
    clientsNeedReview: number;
    clientsReached: number;
  };
  courseId: string;
  created: DateTimeModel;
  currentValue: number;
  description: string;
  endDate: Nullable<DateTimeModel>;
  goalId: string;
  iconName: Nullable<string> = null;
  initialValue: number;
  isClientManaged: boolean;
  lastActivity: Nullable<DateTimeModel>;
  parentSubgoalId: Nullable<string>;
  programId: string;
  progress: number;
  reached: Nullable<DateTimeModel>;
  reviewUntil: Nullable<DateTimeModel>;
  sortOrder: number;
  startDate: Nullable<DateTimeModel>;
  status: SubgoalModel.Status;
  targetValue: number;
  title: string;
  unit: Nullable<string>;
  updated: DateTimeModel;
  userId: string;

  constructor(props: SubgoalModel.Props) {
    super(props);
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<SubgoalModel, SubgoalModel.Props>(props),
    );
  }

  get durationString() {
    if (!this.startDate || !this.endDate) {
      return '';
    }

    return `${this.startDate.format('ll').replace(/\s/g, '\u00A0')} — ${this.endDate
      .format('ll')
      .replace(/\s/g, '\u00A0')}`;
  }

  get icon() {
    return getSubgoalIcon(this.iconName);
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  getRenderColor(theme: Theme) {
    return getSubgoalColor(this.color, theme);
  }
}

export namespace SubgoalModel {
  export type Checkpoint = {
    reached: Nullable<DateTimeModel>;
    title: string;
    uuid: string;
  };

  export type Props = BaseModel.ModelToProps<
    SubgoalModel,
    'clone' | 'durationString' | 'getRenderColor' | 'icon'
  >;

  export type Status = SubgoalApiModel['status'];
}
